/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021 Open Assessment Technologies SA;
 */

/**
 * Helper function for constraints validation during resize operations
 * Used for one axis
 * @param {Number} coord requested coordinate
 * @param {Number} initialCoord initial coordinate of the pointer
 * @param {Number} maxCoord maximum allowed coordinate (meant to be window size)
 * @param {Number} vectorP resizeVector projection on the move axis
 * @param {Number} initialBoxCoord initial box position coordinate
 * @param {Number} initialSize initial size of the box
 * @param {Number} minSize minimal allowed box size
 * @returns {Number} coordinate with constraints applied
 */
function ensureResizeConstraints(coord, initialCoord, maxCoord, vectorP, initialBoxCoord, initialSize, minSize) {
    const initialCoordBoxOffset = initialCoord - initialBoxCoord;
    if (vectorP > 0) {
        return Math.min(
            Math.max(coord, initialCoord - (initialSize - minSize)),
            maxCoord + initialCoordBoxOffset - initialSize
        );
    } else if (vectorP < 0) {
        return Math.min(Math.max(coord, initialCoordBoxOffset), initialCoord + (initialSize - minSize));
    } else {
        return initialCoord;
    }
}

/**
 * Helper function for constraints validation during dragmove operations
 * Used for one axis
 * @param {Number} coord requested coordinate
 * @param {Number} initialCoord  initial coordinate of the pointer
 * @param {Number} initialBoxCoord initial box position coordinate
 * @param {Number} boxSize box size
 * @param {Number} maxCoord  maximum allowed coordinate (meant to be window size)
 * @returns {Number} coordinate with constraints applied
 */
function ensureDragConstraints(coord, initialCoord, initialBoxCoord, boxSize, maxCoord) {
    if (coord < initialCoord) {
        return initialCoord - coord > initialBoxCoord ? initialCoord - initialBoxCoord : coord;
    } else {
        return coord - initialCoord > maxCoord - boxSize - initialBoxCoord
            ? maxCoord - boxSize - initialBoxCoord + initialCoord
            : coord;
    }
}

export { ensureResizeConstraints, ensureDragConstraints };
